/**
 * External dependencies
 */
import React from "react";
import { connect } from "react-redux";

/**
 * Internal dependencies
 */
import Main from "components/main";
import DocumentHead from "data/document-head";
import Navigation from "modules/me/navigation";
import Section from "components/section";
import SettingsRow from "modules/me/setting-row";
import SettingsGroup from "modules/me/setting-row/settings-group";
import FormToggle from "components/forms/form-toggle";
import PageHeader from "layout/page-header";
import { savePreference, setPreference } from "state/preferences/actions";
import { getNormalizedPreferences } from "state/preferences/selectors";
import { successNotice, errorNotice } from "state/notices/actions";
/**
 * Style dependencies
 */
import "./styles.scss";
import FormTextInput from "../../../components/forms/form-text-input";
import Button from "../../../components/button";
import vd from "lib/vd";
import { Stack } from "@towersystems/towersystems-design-system";
import Preloader from "../../../components/preloader";

export const options = [
  { label: "Today", value: "Today" },
  { label: "This week", value: "This week" },
  { label: "This month", value: "This month" },
  { label: "Last 30 days", value: "Last 30 days" },
  { label: "Last 60 days", value: "Last 60 days" },
  { label: "Last 180 days", value: "Last 180 days" },
  { label: "This year", value: "This year" },
  { label: "Last year", value: "Last year" },
];

const AccountPreferences = (props) => {
  const [locations, setLocations] = React.useState(undefined);

  const [sendingMarketingEmail, setSendingMarketingEmail] =
    React.useState(false);

  const [localInput, updateLocalInput] = React.useState({
    preferredEmail: props.preferences.preferredEmail,
    marketingEmailLocations: props.preferences.marketingEmailLocations ?? [],
    weeklyReportEnabled: props.preferences.weeklyReportEnabled,
    monthlyReportEnabled: props.preferences.monthlyReportEnabled,
  });

  React.useEffect(() => {
    vd.location()
      .listAll()
      .then((locations) => {
        setLocations(locations || []);
      })
      .catch((err) => console.log);
  }, []);

  const handleOnCheckedMarketingEmailChange = (checked) => {
    handleUpdateLocalInput("marketingEmailLocations", checked);
    return handleOnSavePreferences("marketingEmailLocations", checked);
  };

  const handleOnSavePreferences = (key, value) => {
    if (props.savePreference) {
      const originalValue = props.preferences[key];
      return props
        .savePreference(key, value)
        .then((result) => {
          props.successNotice("Saved preferences");
        })
        .catch((err) => {
          props.errorNotice("Failed to save preferences");
        });
    }
  };

  const handleUpdateLocalInput = (key, value) => {
    updateLocalInput({
      ...localInput,
      [key]: value,
    });
  };

  const handleOnInputBlur = (key) => {
    return handleOnSavePreferences(key, localInput[key]);
  };

  const handleOnSendMarketingEmail = () => {
    if (validEmail) {
      setSendingMarketingEmail(true);
      return vd
        .marketing()
        .sendSubcribedEmails()
        .then((result) => {
          if (result.success) {
            props.successNotice("Emails added to queue");
          } else {
            props.errorNotice("Limit Reached. Try again in 5 minutes.");
          }
        })
        .catch((err) => {
          console.log(err);
          props.errorNotice("Failed to send marketing email");
        })
        .finally(() => {
          setSendingMarketingEmail(false);
        });
    }
  };

  const validEmail = React.useMemo(() => {
    const stringToTest = localInput.preferredEmail || "";
    return stringToTest.match(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    );
  }, [localInput.preferredEmail]);

  const dirtyEmail = React.useMemo(() => {
    return localInput.preferredEmail !== props.preferences.preferredEmail;
  }, [localInput.preferredEmail, props.preferences.preferredEmail]);

  return (
    <>
      <PageHeader title="Settings" />
      <Main>
        <DocumentHead title="Preferences" />
        <Navigation section="preferences" />
        <Section title="Site Preferences" padded={false}>
          <SettingsGroup>
            <SettingsRow
              label="Layout editable"
              altDescription="Prevent user from resizing, dragging, adding widgets or adding layouts."
            >
              <FormToggle
                id="toggle-layout-editable"
                wrapperClassName="toggle-layout-editable"
                onChange={(r) => handleOnSavePreferences("layoutEdit", r)}
                checked={props.preferences.layoutEdit}
              />
            </SettingsRow>
            <SettingsRow
              label="Show grid"
              altDescription="Display a visible grid behind background."
            >
              <FormToggle
                id="toggle-grid"
                wrapperClassName="toggle-grid"
                onChange={(r) => handleOnSavePreferences("gridVisible", r)}
                checked={props.preferences.gridVisible}
              />
            </SettingsRow>
            <SettingsRow
              label="Default date range"
              altDescription="Set the initial date range the dashboard will load."
            >
              <select
                defaultValue={props.dateRange}
                onChange={(e) =>
                  handleOnSavePreferences("dateRange", e.target.value)
                }
                value={props.preferences.dateRange}
              >
                {options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </SettingsRow>
          </SettingsGroup>
        </Section>

        <Section title="Site Preferences" padded={false}>
          <SettingsGroup>
            <SettingsRow
              label="Receive Marketing"
              altDescription="Enable emails."
            >
              <FormToggle
                id="toggle-receive-marketing"
                wrapperClassName="toggle-receive-marketing"
                onChange={(r) =>
                  handleOnSavePreferences("receiveMarketingEmails", r)
                }
                checked={props.preferences.receiveMarketingEmails}
              />
            </SettingsRow>

            <SettingsRow
              label="Receive Marketing Variants"
              altDescription="Select time range to receive marketing emails."
            >
              <Stack space="medium">
                {[
                  {
                    name: "Weekly",
                    settingValue: "weeklyReportEnabled",
                  },
                  {
                    name: "Monthly",
                    settingValue: "monthlyReportEnabled",
                  },
                ].map((j) => {
                  const isChecked = localInput[j.settingValue];
                  return (
                    <div>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        value={j.settingValue}
                        onChange={() => {
                          if (isChecked) {
                            handleUpdateLocalInput(j.settingValue, false);
                            handleOnSavePreferences(j.settingValue, false);
                          } else {
                            handleUpdateLocalInput(j.settingValue, true);
                            handleOnSavePreferences(j.settingValue, true);
                          }
                        }}
                      />
                      <label>{j.name}</label>
                    </div>
                  );
                })}
              </Stack>
            </SettingsRow>

            <SettingsRow
              altDescription="Selected locations will receive marketing email."
              label="Locations"
            >
              <Stack space="medium">
                {locations ? (
                  locations.map((location) => {
                    const isChecked =
                      localInput.marketingEmailLocations.includes(
                        location.locationCode
                      );
                    return (
                      <div key={location.id}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          value={location.locationCode}
                          onChange={() => {
                            if (isChecked) {
                              handleOnCheckedMarketingEmailChange(
                                localInput.marketingEmailLocations.filter(
                                  (l) => l !== location.locationCode
                                )
                              );
                            } else {
                              handleOnCheckedMarketingEmailChange([
                                ...localInput.marketingEmailLocations,
                                location.locationCode,
                              ]);
                            }
                          }}
                        />
                        <label>{location.name}</label>
                      </div>
                    );
                  })
                ) : (
                  <Preloader />
                )}
              </Stack>
            </SettingsRow>
            <SettingsRow
              label="Email Preference"
              altDescription="Set your preferred email address."
            >
              <FormTextInput
                type="email"
                isError={!validEmail}
                onChange={(r) =>
                  handleUpdateLocalInput("preferredEmail", r.target.value)
                }
                onBlur={() => handleOnInputBlur("preferredEmail")}
                value={localInput.preferredEmail}
              />
            </SettingsRow>
            <SettingsRow
              label="Send Marketing Email"
              altDescription="Send a copy of marketing email to your preferred email address."
            >
              <Button
                disabled={!validEmail || sendingMarketingEmail || dirtyEmail}
                onClick={() => handleOnSendMarketingEmail()}
              >
                Send email
              </Button>
            </SettingsRow>
          </SettingsGroup>
        </Section>
      </Main>
    </>
  );
};
export default connect(
  (state) => ({
    preferences: getNormalizedPreferences(state),
  }),
  {
    successNotice,
    errorNotice,
    savePreference,
    setPreference,
  }
)(AccountPreferences);
